import { useFhirData } from "../../../utils/useFhirData";
import { Diagnosis } from "./patientSummaryAccordions/Diagnosis/Diagnosis";
import { Prescriptions } from "./patientSummaryAccordions/Prescriptions/Prescriptions";
import { ServiceRequest } from "./patientSummaryAccordions/ServiceRequest/ServiceRequest";

import { Stack } from "@mui/material";
import { useState } from "react";
import { useSessionContext } from "../../../contexts/SessionContext";
import { useEhrRecordsMobileLayout } from "../context/EhrRecordsLayoutContext";
import { IframeDialog } from "./IframeDialog";
import { Allergies } from "./patientSummaryAccordions/Allergies/Allergies";
import { ClinicalEncounter } from "./patientSummaryAccordions/ClinicalEncounter/ClinicalEncounter";
import { ClinicalEncounterVisualizer } from "./patientSummaryAccordions/ClinicalEncounter/ClinicalEncounterVisualizer";
import { Documents } from "./patientSummaryAccordions/Documents/Documents";
import { ObservationsEncounter } from "./patientSummaryAccordions/ObservationsEncounter/ObservationsEncounter";
import { ProcedureRequest } from "./patientSummaryAccordions/ProcedureRequest/ProcedureRequest";
import { Problems } from "./patientSummaryAccordions/Problems/Problems";
import { ProblemsConducts } from "./patientSummaryAccordions/Problems/ProblemsConducts";
import { Immunizations } from "./patientSummaryAccordions/Immunizations/Immunization";
import { CarePlan } from "./patientSummaryAccordions/CarePlan/CarePlan";
import { Questionnaires } from "./patientSummaryAccordions/Questionnaires/Questionnaires";

export function PatientSummary({ summary, setRefetch }: any) {
  const { access } = useSessionContext();
  const isEhrMobile = useEhrRecordsMobileLayout();
  const [open, setOpen] = useState<boolean>(false);
  const [responseViewer, setResponseViewer] = useState<boolean>(false);
  const [selectedResponse, setSelectedResponse] = useState<any>();
  const [binaryUrl, setBinaryUrl] = useState<string>("");
  const [documentComment, setDocumentComment] = useState<string>("");

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setBinaryUrl("");
    setDocumentComment("");
    setOpen(false);
  };

  const handleCloseResponseViewer = () => {
    setSelectedResponse(undefined);
    setResponseViewer(false);
  };

  const prescriptionDocuments = useFhirData.extractPatientSummaryResource(
    summary,
    "DocumentReference",
    "prescricao",
  );

  const medicalNoteData = useFhirData.extractPatientSummaryResource(
    summary,
    "DocumentReference",
    "prescricao",
  )?.[0]?.entry;

  const solicitationData = useFhirData.extractPatientSummaryResource(
    summary,
    "DocumentReference",
    "service-request",
  )?.[0]?.entry;

  const NoteReportData = useFhirData.extractPatientSummaryResource(
    summary,
    "DocumentReference",
    "atestado",
  )?.[0]?.entry;

  const resultsData = useFhirData.extractPatientSummaryResource(
    summary,
    "DocumentReference",
    "resultado",
  );

  const observationsData = useFhirData.extractPatientSummaryResource(
    summary,
    "Observation",
  );

  const encounterData = useFhirData.extractPatientSummaryResource(
    summary,
    "Encounter",
  );
  const questionnaireData = useFhirData.extractPatientSummaryResource(
    summary,
    "QuestionnaireResponse",
  );

  const questionnaireResponsesWithUsageTypeExtension =
    questionnaireData?.[0]?.entry?.filter((bundleEntry: any) => {
      const extensions = bundleEntry?.resource?.extension;
      if (!Array.isArray(extensions)) return false;

      return extensions.some(
        (extension: any) =>
          extension?.url ===
          "http://fluxmed.com.br/StructureDefinition/questionnaire-usage-type",
      );
    }) ?? [];

  const auxEncounter = encounterData?.[0]?.entry?.map((el: any) => {
    const encounter = el?.resource;
    const auxObservations = observationsData?.[0]?.entry?.filter((e: any) => {
      const isMatchingEncounter = e?.resource?.encounter?.reference.includes(
        el?.resource?.id,
      );

      const observationCode = e?.resource?.code?.coding?.[0]?.code;
      const isAnamneseCode = [
        "10154-3",
        "10164-2",
        "11348-0",
        "29762-2",
        "10157-6",
        "11369-6",
      ].includes(observationCode);

      return isMatchingEncounter && !isAnamneseCode;
    });

    const auxQuestionnaire = questionnaireData?.[0]?.entry?.filter(
      (e: any) =>
        e?.resource?.encounter?.reference.includes(el?.resource?.id) &&
        e?.resource?.questionnaire === "Questionnaire/clinicalRegister",
    );

    return {
      encounter,
      obs: auxObservations,
      questionnaire: auxQuestionnaire,
    };
  });

  function handleOpenVisualizer(encounterId: string) {
    const findValue = auxEncounter?.find((el: any) => {
      return el?.encounter?.id === encounterId;
    });
    if (findValue) {
      setSelectedResponse(findValue?.questionnaire?.[0]?.resource?.item);
      setResponseViewer(true);
    }
  }

  function verifyVisualizer(encounterId: string) {
    const findValue = auxEncounter?.find((el: any) => {
      return el?.encounter?.id === encounterId;
    });
    return findValue?.questionnaire?.[0]?.resource?.item;
  }

  return (
    <>
      <Stack spacing={isEhrMobile ? 2 : "4px"}>
       
        <Stack paddingBottom={isEhrMobile ? 0 : 4} spacing={isEhrMobile ? 0 : "4px"}>
          <Documents
            setRefetch={setRefetch}
            sign
            handleOpen={handleOpen}
            setBinaryUrl={setBinaryUrl}
            title="Documentos"
            medicalNoteData={medicalNoteData}
            solicitationData={solicitationData}
            resultsData={useFhirData.extractPatientSummaryResource(
              summary,
              "DocumentReference",
              "resultado",
            )}
            NoteReportData={NoteReportData}
            encounterData={encounterData?.[0]?.entry}
            data={summary}
          />
          <Questionnaires
            title="Questionários"
            questionnaireResponses={
              questionnaireResponsesWithUsageTypeExtension
            }
          />
          {access?.type === "professional" ? (
            <Documents
              setRefetch={setRefetch}
              handleOpen={handleOpen}
              setBinaryUrl={setBinaryUrl}
              title="Assinadura pendente"
              medicalNoteData={medicalNoteData?.filter((e: any) => {
                return (
                  e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !==
                  "ProofOfOrigin"
                );
              })}
              solicitationData={solicitationData?.filter((e: any) => {
                return (
                  e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !==
                  "ProofOfOrigin"
                );
              })}
              resultsData={useFhirData.extractPatientSummaryResource(
                summary,
                "DocumentReference",
                "resultado",
              )}
              NoteReportData={NoteReportData?.filter((e: any) => {
                return (
                  e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !==
                  "ProofOfOrigin"
                );
              })}
              encounterData={encounterData?.[0]?.entry}
              data={summary}
            />
          ) : (
            <></>
          )}
        </Stack>

        {access?.type === "professional" && (
          <ClinicalEncounter
            verifyVisualizer={verifyVisualizer}
            title="Atendimentos"
            openVisualizer={handleOpenVisualizer}
            data={auxEncounter}
          />
        )}
        {access?.type === "professional" && (
          <ObservationsEncounter
            verifyVisualizer={verifyVisualizer}
            title="Observações"
            data={auxEncounter}
            openVisualizer={handleOpenVisualizer}
          />
        )}
        <Prescriptions
          verifyVisualizer={verifyVisualizer}
          openVisualizer={handleOpenVisualizer}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
          title="Medicamentos"
          data={
            useFhirData.extractPatientSummaryResource(
              summary,
              "MedicationRequest",
            )?.[0]?.entry
          }
          documentReference={prescriptionDocuments?.[0]?.entry?.filter(
            (el: any) => el?.resource?.resourceType === "DocumentReference",
          )}
        />

        <ServiceRequest
          verifyVisualizer={verifyVisualizer}
          openVisualizer={handleOpenVisualizer}
          setRefetch={setRefetch}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
          setDocumentComment={setDocumentComment}
          title="Exames"
          exames={useFhirData.extractPatientSummaryResource(
            summary,
            "ServiceRequest",
          )}
          data={useFhirData.extractPatientSummaryResource(
            summary,
            "DocumentReference",
            "service-request",
          )}
          resultsData={resultsData}
          serviceRequests={
            useFhirData.extractPatientSummaryResource(
              summary,
              "ServiceRequest",
            )?.[0]?.entry
          }
        />

        <Diagnosis
          verifyVisualizer={verifyVisualizer}
          openVisualizer={handleOpenVisualizer}
          title="Diagnósticos"
          data={useFhirData
            .extractPatientSummaryResource(summary, "Condition")?.[0]
            ?.entry?.filter(
              (el: any) =>
                el?.resource?.category?.[0]?.coding?.[0]?.code !==
                "problem-list-item",
            )}
        />
        <Allergies
          verifyVisualizer={verifyVisualizer}
          openVisualizer={handleOpenVisualizer}
          title="Alergias"
          data={useFhirData.extractPatientSummaryResource(
            summary,
            "AllergyIntolerance",
          )}
        />

        <Immunizations
          verifyVisualizer={verifyVisualizer}
          openVisualizer={handleOpenVisualizer}
          title="Imunizações"
          data={useFhirData.extractPatientSummaryResource(
            summary,
            "Immunization",
          )}
        />

        {access?.type === "professional" && (
          <CarePlan
            verifyVisualizer={verifyVisualizer}
            openVisualizer={handleOpenVisualizer}
            title="Planos de tratamento"
            data={useFhirData.extractPatientSummaryResource(
              summary,
              "CarePlan",
            )}
          />
        )}

        {access?.type === "professional" && (
          <Problems
            verifyVisualizer={verifyVisualizer}
            openVisualizer={handleOpenVisualizer}
            title="Lista de Problemas"
            data={useFhirData
              .extractPatientSummaryResource(summary, "Condition")?.[0]
              ?.entry?.filter(
                (el: any) =>
                  el?.resource?.category?.[0]?.coding?.[0]?.code ===
                  "problem-list-item",
              )}
            personData={
              useFhirData?.extractPatientSummaryResource(
                summary,
                "QuestionnaireResponse",
              )?.[0]?.entry
            }
          />
        )}

        {access?.type === "professional" && (
          <ProblemsConducts
            title="Condutas"
            personData={
              useFhirData?.extractPatientSummaryResource(
                summary,
                "QuestionnaireResponse",
              )?.[0]?.entry
            }
            openVisualizer={handleOpenVisualizer}
            verifyVisualizer={verifyVisualizer}
          />
        )}

        <ProcedureRequest
          verifyVisualizer={verifyVisualizer}
          openVisualizer={handleOpenVisualizer}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
          title="Procedimentos"
          procedimentos={useFhirData.extractPatientSummaryResource(
            summary,
            "ServiceRequest",
          )}
          data={useFhirData.extractPatientSummaryResource(
            summary,
            "DocumentReference",
            "service-request",
          )}
          resultsData={resultsData}
        />

        {responseViewer && selectedResponse && (
          <ClinicalEncounterVisualizer
            data={selectedResponse}
            handleClose={handleCloseResponseViewer}
            isOpen={responseViewer}
          />
        )}

        {open && (
          <IframeDialog
            setRefetchSummary={setRefetch}
            handleClose={handleClose}
            isOpen={open}
            data={binaryUrl}
            commentText={documentComment}
          />
        )}
      </Stack>
    </>
  );
}
